import type { HistoryChange } from './history.model';

import { on } from '@ngrx/store';
import { createImmerReducer } from 'ngrx-immer/store';

import { HistoryChangeAction } from './history.model';
import HistoryActions from './history.actions';

export const HISTORY_FEATURE_KEY = 'history';

const MAX_HISTORY_LENGTH = 50;

/**
 * A separate, smaller cap for AI-originated changes only — decided alongside "1 model reply = 1
 * undo step" (ticket #69719 §1/§6). Independent of `MAX_HISTORY_LENGTH`: evicting the oldest
 * AI-tagged entry once 20 are present never touches a manual edit sitting in between.
 */
const MAX_AI_HISTORY_LENGTH = 20;

const isAiBatchChange = (change: HistoryChange): boolean =>
    change.action === HistoryChangeAction.batch && change.origin === 'ai';

export type HistoryState = {
    prev: HistoryChange[];
    next: HistoryChange[];
};

export const initialHistoryState: HistoryState = {
    prev: [],
    next: [],
};

export const historyReducer = createImmerReducer<HistoryState>(
    initialHistoryState,
    on(HistoryActions.resetHistory, () => initialHistoryState),
    on(HistoryActions.addChangeToHistory, (state, { change }) => {
        if (isAiBatchChange(change)) {
            const aiCount = state.prev.filter(isAiBatchChange).length;

            if (aiCount >= MAX_AI_HISTORY_LENGTH) {
                const oldestAiIndex = state.prev.findIndex(isAiBatchChange);

                state.prev.splice(oldestAiIndex, 1);
            }
        }

        if (state.prev.length >= MAX_HISTORY_LENGTH) {
            state.prev.shift();
        }
        state.prev.push(change);
        state.next = [];

        return state;
    }),
    on(HistoryActions.undoSuccess, (state) => {
        const lastPrev = state.prev.pop();
        state.next = [lastPrev, ...state.next];

        return state;
    }),
    on(HistoryActions.redoSuccess, (state) => {
        const lastNext = state.next.shift();
        state.prev = [...state.prev, lastNext];

        return state;
    })
    // on(HistoryActions.loadHistorySuccess, (state: HistoryState, { history }) => ({ ...state, history, loaded: true })),
    // on(HistoryActions.loadHistoryFailure, (state: HistoryState, { error }) => ({ ...state, error })),
);
